import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  CopyButton,
  EmptyHint,
  ListInput,
  Panel,
  ResetButton,
  usePersistentState,
  useSfx,
} from "@/components/tools/ToolKit";
import {
  DEFAULT_CLASS_LIST,
  dedupe,
  parseList,
  shuffle,
} from "@/lib/random-engine";
import { LayoutGrid, Printer, Shuffle, RotateCcw, Download, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";

export type LayoutMode = "rows" | "pairs" | "groups" | "ushape";

export function SeatingChart() {
  const [raw, setRaw] = usePersistentState("pickamini:seating", DEFAULT_CLASS_LIST.join("\n"));
  const [soundOn, setSoundOn] = usePersistentState("pickamini:seating:sound", true);
  const [columns, setColumns] = useState(4);
  const [layout, setLayout] = useState<LayoutMode>("rows");
  const [seats, setSeats] = useState<string[]>([]);
  const sfx = useSfx(soundOn);

  const names = dedupe(parseList(raw));

  const randomiseSeats = () => {
    if (names.length < 2) return;
    sfx.click();
    setSeats(shuffle(names));
    toast.success("Randomised seating plan!");
  };

  const clearSeats = () => {
    setSeats([]);
  };

  const getGridCols = () => {
    switch (layout) {
      case "pairs":
        return 2;
      case "groups":
        return 4;
      case "ushape":
        return 3;
      case "rows":
      default:
        return columns;
    }
  };

  const cols = getGridCols();

  const asText = seats.length
    ? seats.map((name, i) => `Seat ${i + 1}: ${name}`).join("\n")
    : "";

  const downloadCsv = () => {
    if (seats.length === 0) return;
    let csv = "Seat Number,Student Name,Layout Type\n";
    seats.forEach((name, idx) => {
      csv += `"${idx + 1}","${name.replace(/"/g, '""')}","${layout}"\n`;
    });
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `seating-chart-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Downloaded CSV Seating Plan");
  };

  return (
    <div className="grid gap-5 lg:grid-cols-[340px_1fr]">
      {/* Settings Panel */}
      <Panel title="Class & Room Setup" className="no-print space-y-4">
        <ListInput value={raw} onChange={setRaw} label="Student Names" count={names.length} />

        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Seating Arrangement
          </p>
          <div className="grid grid-cols-2 gap-1.5">
            {[
              { id: "rows", label: "Grid Rows" },
              { id: "pairs", label: "Pairs (2x2)" },
              { id: "groups", label: "Groups (4x4)" },
              { id: "ushape", label: "U-Shape" },
            ].map((m) => (
              <Button
                key={m.id}
                type="button"
                size="sm"
                variant={layout === m.id ? "default" : "outline"}
                onClick={() => {
                  setLayout(m.id as LayoutMode);
                  sfx.click();
                }}
                className="text-xs justify-start font-medium"
              >
                {m.label}
              </Button>
            ))}
          </div>
        </div>

        {layout === "rows" && (
          <div>
            <label className="block text-sm font-medium">
              Columns per row
              <Input
                type="number"
                min={2}
                max={8}
                value={columns}
                onChange={(e) => setColumns(Math.min(8, Math.max(2, Number(e.target.value))))}
                className="mt-1 font-bold"
              />
            </label>
          </div>
        )}

        <div className="flex flex-wrap gap-2 pt-2">
          <Button
            size="lg"
            onClick={randomiseSeats}
            disabled={names.length < 2}
            className="w-full font-bold"
          >
            <Shuffle className="size-4 mr-2" />
            {seats.length ? "Randomise Again" : "Build Seating Chart"}
          </Button>

          <ResetButton onClick={clearSeats} />
        </div>

        {names.length < 2 && (
          <div className="mt-3">
            <EmptyHint>Add at least 2 names to generate seats.</EmptyHint>
          </div>
        )}
      </Panel>

      {/* Seating Map Container */}
      <div>
        {seats.length > 0 ? (
          <>
            {/* Action Bar for Print & Export */}
            <div className="no-print mb-4 flex flex-wrap items-center justify-between gap-2 border-b pb-3">
              <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                <LayoutGrid className="size-4 text-primary" />
                <span>
                  {seats.length} Students Assigned ({layout.toUpperCase()} Layout)
                </span>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <CopyButton text={asText} label="Copy Seats" />

                <Button variant="outline" size="sm" onClick={() => window.print()}>
                  <Printer className="size-3.5 mr-1.5" /> Print Chart
                </Button>

                <Button variant="outline" size="sm" onClick={downloadCsv}>
                  <FileSpreadsheet className="size-3.5 mr-1.5" /> Download CSV
                </Button>
              </div>
            </div>

            {/* PRINT ONLY PROFESSIONAL HEADER */}
            <div className="hidden print:block mb-6 text-center border-b pb-4">
              <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">
                Classroom Seating Chart
              </h1>
              <p className="text-sm text-slate-600 mt-1">
                Date: {new Date().toLocaleDateString()} • {seats.length} Total Students • Layout: {layout.toUpperCase()}
              </p>
            </div>

            {/* Main Classroom Floor Plan Box */}
            <div className="surface-card print-clean p-6 rounded-2xl bg-card border shadow-soft print:shadow-none print:border-slate-300">
              {/* Front of Class / Board Indicator */}
              <div className="mb-6 rounded-xl bg-primary/10 border border-primary/20 py-3 text-center print:bg-slate-100 print:border-slate-300">
                <span className="font-display text-xs sm:text-sm font-bold uppercase tracking-[0.2em] text-primary print:text-slate-800">
                  ✏️ FRONT OF CLASS / TEACHER BOARD ✏️
                </span>
              </div>

              {/* Seating Grid */}
              <div
                className="grid gap-3 sm:gap-4 print:gap-3"
                style={{
                  gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
                }}
              >
                {seats.map((student, i) => (
                  <div
                    key={`${student}-${i}`}
                    className="group relative flex flex-col items-center justify-center rounded-xl border border-border bg-secondary/30 p-3.5 text-center shadow-sm transition-all hover:border-primary/40 hover:bg-card print:border-slate-300 print:bg-white print:shadow-none"
                  >
                    <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-0.5 print:text-slate-500">
                      Desk {i + 1}
                    </span>
                    <span className="font-display text-sm sm:text-base font-bold text-foreground truncate max-w-full print:text-slate-900">
                      {student}
                    </span>
                  </div>
                ))}
              </div>

              {/* Rear of Class Indicator */}
              <div className="mt-8 pt-3 border-t text-center text-xs font-semibold uppercase tracking-widest text-muted-foreground/60 print:text-slate-400">
                Back of Room
              </div>
            </div>
          </>
        ) : (
          <div className="surface-card grid min-h-[300px] place-items-center p-8 text-center text-muted-foreground rounded-2xl">
            <div className="flex flex-col items-center gap-2 max-w-sm">
              <LayoutGrid className="size-10 text-muted-foreground/60" />
              <p className="font-semibold text-foreground">No seating chart generated</p>
              <p className="text-xs text-muted-foreground">
                Add student names in the setup panel and click Build Seating Chart to generate your classroom desk plan.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
